// Client-side export of the CATEGORIZED policy content shown in the Document
// preview modal (the policy-categorization flow's output). The per-topic content
// served by /store/documents/policy-scores is already markdown-ish — "### "
// headings, "- " bullets, **bold** (see renderPolicyContent.jsx) — so a Markdown
// file is a faithful round-trip of what the modal renders, with no backend work.
import { formatTopicKey } from "./policyFormatters.js";

// Non-filename-safe characters -> "-", so a topic label like "Returns, refunds
// and cancellations" becomes "returns-refunds-and-cancellations".
const slugify = text =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "policy";

// One topic -> "## <Title> (score/100)" + its sections' content.
// Topics with no content still get their heading so the export mirrors the tabs
// in the modal, making it obvious which topics are empty.
const topicToMarkdown = topic => {
  const score = topic.score == null ? null : Math.round(topic.score);
  const heading = `## ${formatTopicKey(topic.key)}${score != null ? ` — Score: ${score}/100` : ""}`;
  const sections = (topic.sections || []).filter(s => (s.content || "").trim());
  if (sections.length === 0) return `${heading}\n\n_No content available for this topic._`;
  return `${heading}\n\n${sections.map(s => s.content.trim()).join("\n\n")}`;
};

// Full export: every categorized topic, in the order the modal tabs show them.
export const buildPolicyMarkdown = (policyTopics = [], overallScore = null) => {
  const score = overallScore == null ? null : Math.round(overallScore);
  const header = `# Categorized policies${score != null ? `\n\nOverall score: ${score}/100` : ""}`;
  const body = policyTopics.map(topicToMarkdown).join("\n\n---\n\n");
  return `${header}\n\n${body}\n`;
};

// Single-topic export, for the topic currently open in the modal.
export const buildTopicMarkdown = topic => (topic ? `${topicToMarkdown(topic)}\n` : "");

export const policyFileName = (topic = null) =>
  topic ? `categorized-policy-${slugify(formatTopicKey(topic.key))}.md` : "categorized-policies.md";

// Trigger a browser download of `text` as `fileName`. Uses a blob URL (revoked
// after the click) rather than a data: URI so large exports aren't capped by
// URL-length limits.
export const downloadTextFile = (text, fileName) => {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
